import { useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Form } from 'react-bootstrap';
import Select, { components } from 'react-select';
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { IoMdClose } from "react-icons/io";
import classNames from "classnames";
import moment from 'moment';
import _ from 'lodash';

// Custom Component
import Breadcrumb from "../../components/Common/Breadcrumb";
import PlayerForm from '../../components/Form/PlayerForm';
import ErrorText from '../../components/TextField/ErrorText';
import { PrimaryButton } from "../../components/Button";
import ErrorAlert from '../../components/Alert/ErrorAlert';

// Images
// import { User, Users, Men, Women } from '../../assets/images/svg';
import { Men, Women } from '../../assets/images/svg';

// Utils
import { detectMenEvent, detectWomenEvent, isDoubleEvent, convertEventType, convertAgeGroup, getPlayerCount, getGenderCount } from '../../utils/helper';

// API Service
import { getCustomerSingleTournamentService, enterTournamentService, editCustomerPlayerService } from '../../service/tournament.service';
import { getCustomerTShirtService } from '../../service/tshirt.service';

const EnterTournament = () => {
    const { state } = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [tournament, setTournament] = useState();
    const [tShirtList, setTShirtList] = useState([]);
    const [playerIdList, setPlayerIdList] = useState([]);

    // const categoryTypeList = [
    //     "INDIVIDUAL",
    //     "TEAM"
    // ]

    const { register, control, watch, setValue, handleSubmit, formState: { errors } } = useForm({
        defaultValues: {
            category_type: {
                value: "INDIVIDUAL",
                label: "INDIVIDUAL"
            }
        },
        mode: "all"
    })

    const { fields, append, remove } = useFieldArray({
        control,
        name: "player_details"
    });

    const watchCategoryType = watch("category_type")
    const watchTeamEventId = watch("team_event_id")
    const watchEventType = watch("event_type")
    const allWatchedValues = watch({
        nest: true
    });

    const reactSelectCustomStyle = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            width: "300px",
            border: state?.isFocused && "1px solid #FA5000",
            boxShadow: state?.isFocused && "0px 0px 2px #FA5000",
            backgroundColor: state?.hasValue ? "#FA5000" : "white",
            "&:hover": {
                border: "1px solid #FA5000",
                boxShadow: "0px 0px 2px #FA5000"
            }
        }),
        option: (styles, { isSelected }) => {
            return {
                ...styles,
                backgroundColor: isSelected ? '#FA5000' : 'white',
                "&:hover": {
                    backgroundColor: "#FA5000",
                    color: 'white'
                }
            }
        },
        singleValue: (provided) => ({
            ...provided,
            color: 'white',
            backgroundColor: "#FA5000"
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#CCCCCC'
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#CCCCCC'
        })
    }

    /**
        * @function fetchCustomerSingleTournament
        * @params
        * @description fetch the single tournament details for customer
    */
    const fetchCustomerSingleTournament = async () => {
        try {
            const result = await getCustomerSingleTournamentService(state?.tournamentId);
            if (result?.data?.status) {
                setTournament(result?.data?.data)
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
        } catch (error) {
            ErrorAlert(error)
        }
    }

    /**
        * @function fetchCustomerTShirt
        * @params
        * @description fetch the single tournament details for customer
    */
    const fetchCustomerTShirt = async () => {
        try {
            const result = await getCustomerTShirtService({
                id: state?.tournamentId
            });
            if (result?.data?.status) {
                setTShirtList(result?.data?.data)
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
        } catch (error) {
            ErrorAlert(error)
        }
    }

    useEffect(() => {
        if (watchCategoryType?.value === "INDIVIDUAL" && watchEventType && (watchEventType !== "GROUP_CAPTAIN" && watchEventType !== "JUNIOR_SINGLE") && allWatchedValues[`${watchEventType}_age_group`] && allWatchedValues[`${watchEventType}_age_value`]) {
            if (allWatchedValues?.player_details?.length > getPlayerCount(watchEventType)) {
                setValue('player_details', allWatchedValues?.player_details?.slice(0, getPlayerCount(watchEventType)));
            } else {
                setValue('player_details', Array.from({
                    length: getPlayerCount(watchEventType)
                }, (_, i) => allWatchedValues?.player_details?.length >= i ? allWatchedValues?.player_details?.[i] : {
                    player_name: "",
                    club_name: "",
                    player_email: "",
                    player_contact: "",
                    player_gender: "MALE",
                    player_dob: "",
                    player_idcard: "",
                    nationality: "",
                    ...(tournament?.sports_type === "PICKLEBALL" && {
                        dupr_rating: ""
                    }),
                    proffesional_player: false
                }));
            }
        } else if (watchCategoryType?.value === "INDIVIDUAL" && watchEventType && (watchEventType === "GROUP_CAPTAIN" || watchEventType !== "JUNIOR_SINGLE") && allWatchedValues[`${watchEventType}_age_group`] && allWatchedValues[`${watchEventType}_age_value`]) {
            if (allWatchedValues?.player_details?.length === 0) {
                setValue('player_details', Array.from({
                    length: 1
                }, (_, i) => allWatchedValues?.player_details?.length >= i ? allWatchedValues?.player_details?.[i] : {
                    player_name: "",
                    club_name: "",
                    player_email: "",
                    player_contact: "",
                    player_gender: "MALE",
                    player_dob: "",
                    player_idcard: "",
                    nationality: "",
                    ...(tournament?.sports_type === "PICKLEBALL" && {
                        dupr_rating: ""
                    }),
                    proffesional_player: false
                }));
            } else if (allWatchedValues?.player_details?.length > 0 && allWatchedValues?.player_details?.length > getPlayerCount(watchEventType)) {
                setValue('player_details', allWatchedValues?.player_details?.slice(0, getPlayerCount(watchEventType)));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tournament, watchEventType, allWatchedValues[`${watchEventType}_age_group`], allWatchedValues[`${watchEventType}_age_value`]])

    useEffect(() => {
        if (state?.tournamentId) {
            fetchCustomerSingleTournament()
            fetchCustomerTShirt()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state])

    useEffect(() => {
        if (tournament) {
            if (state?.edit) {
                const player_details = []
                if ((watchCategoryType?.value === "INDIVIDUAL" || (!state?.player_details?.every(item => item?.team_name))) && state?.player_details?.length > 0) {
                    setValue("category_type", {
                        value: "INDIVIDUAL",
                        label: "INDIVIDUAL"
                    })
                    setValue("event_type", state?.player_details[0]?.event_type)
                    setValue(`${state?.player_details[0]?.event_type}_age_group_value`, {
                        event_type: state?.player_details[0]?.event_type,
                        age_group: state?.player_details[0]?.age_group,
                        age_value: state?.player_details[0]?.age_value
                    });
                    setValue(`${state?.player_details[0]?.event_type}_age_group`, state?.player_details[0]?.age_group)
                    setValue(`${state?.player_details[0]?.event_type}_age_value`, state?.player_details[0]?.age_value)
                }
                if ((watchCategoryType?.value === "TEAM" || (state?.player_details?.every(item => item?.team_name))) && state?.player_details?.length > 0) {
                    setValue("category_type", {
                        value: "TEAM",
                        label: "TEAM"
                    })
                    setValue("team_event_id", {
                        value: tournament?.team_event_details?.find(item => state?.player_details?.find(player => player?.team_event_id)?.team_event_id === item?.id)?.id,
                        label: tournament?.team_event_details?.find(item => state?.player_details?.find(player => player?.team_event_id)?.team_event_id === item?.id)?.event_name
                    })
                    setValue("team_name", state?.player_details?.find(player => player?.team_event_id)?.team_name)
                }
                state?.player_details?.forEach((item) => {
                    player_details.push({
                        player_name: item?.player_name,
                        club_name: item?.club_name,
                        player_email: item?.player_email,
                        player_contact: item?.player_contact,
                        player_gender: item?.player_gender,
                        player_dob: new Date(item?.player_dob),
                        player_idcard: item?.player_idcard,
                        nationality: item?.nationality,
                        ...(tournament?.sports_type === "PICKLEBALL" && {
                            dupr_rating: item?.dupr_rating
                        }),
                        proffesional_player: item?.proffesional_player,
                        ...(item?.tshirt_details && {
                            tshirt_id: {
                                value: item?.tshirt_details?.tshirt?.id,
                                label: item?.tshirt_details?.tshirt?.tshirt_name
                            }
                        }),
                        ...(item?.tshirt_details && {
                            tshirt_size_id: {
                                value: item?.tshirt_details?.size?.id,
                                label: item?.tshirt_details?.size?.size
                            }
                        }),
                    })
                })
                setPlayerIdList(state?.player_details?.map(item => item?.id))
                setValue('player_details', player_details);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, tournament])

    useEffect(() => {
        if (tournament) {
            if ((tournament?.event_details?.filter(item => !item?.team_event_id)?.length === 0 || (tournament?.team_event_details?.length === 0 || !tournament?.team_event_details))) {
                if (tournament?.event_details?.filter(item => !item?.team_event_id)?.length === 0) {
                    setValue("category_type", {
                        value: "TEAM",
                        label: "TEAM"
                    })
                } else if (tournament?.team_event_details?.length === 0 || !tournament?.team_event_details) {
                    setValue("category_type", {
                        value: "INDIVIDUAL",
                        label: "INDIVIDUAL"
                    })
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tournament])

    useEffect(() => {
        if (watchCategoryType?.value === "TEAM" && watchTeamEventId?.value) {
            if ([...new Map(tournament?.event_details?.filter(item => item?.team_event_id === watchTeamEventId?.value)?.map(item => [item?.event_type, item])).values()]?.map(item => item?.event_type)?.length > 0) {
                let playersCount = 0;
                [...new Map(tournament?.event_details?.filter(item => item?.team_event_id === watchTeamEventId?.value)?.map(item => [item?.event_type, item])).values()]?.map(item => item?.event_type)?.forEach((eventItem) => {
                    playersCount += tournament?.event_details?.filter(item => item?.team_event_id === watchTeamEventId?.value && item?.event_type === eventItem)?.length * getPlayerCount(eventItem)
                })
                setValue('player_details', Array.from({
                    length: playersCount
                }, () => ({
                    player_name: "",
                    club_name: "",
                    player_email: "",
                    player_contact: "",
                    player_gender: "MALE",
                    player_dob: "",
                    player_idcard: "",
                    nationality: "",
                    ...(tournament?.sports_type === "PICKLEBALL" && {
                        dupr_rating: ""
                    }),
                    proffesional_player: false
                })));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchTeamEventId])

    /**
        * @function onSubmit
        * @params formdata
        * @description used to create player
    */
    const onSubmit = async (formdata) => {
        try {
            const data = new FormData()
            if (watchCategoryType?.value === "INDIVIDUAL") {
                if (!formdata?.event_type) {
                    ErrorAlert('You must select a event')
                    return
                }
                if (formdata?.event_type) {
                    if (!formdata[`${formdata?.event_type}_age_group`] || !formdata[`${formdata?.event_type}_age_value`]) {
                        ErrorAlert('You must select a event')
                        return
                    }
                }
                if (isDoubleEvent(formdata?.event_type) && formdata[`${formdata?.event_type}_age_group`] === "SEMI_PRO" && formdata?.player_details?.every(item => !item?.proffesional_player)) {
                    ErrorAlert("Tournament requires at least one 'State/District/National Player'.")
                    return
                }
                if (isDoubleEvent(formdata?.event_type) && formdata[`${formdata?.event_type}_age_group`] === "SEMI_PRO" && formdata?.player_details?.every(item => item?.proffesional_player)) {
                    ErrorAlert("Tournament requires at least one 'Amateur Player'.")
                    return
                }
                const genderCount = getGenderCount(formdata?.event_type);

                if (genderCount) {
                    const maleCount = formdata?.player_details?.filter(item => item.player_gender === "MALE")?.length || 0;
                    const femaleCount = formdata?.player_details?.filter(item => item.player_gender === "FEMALE")?.length || 0;

                    if (maleCount !== genderCount?.number_of_males) {
                        ErrorAlert(`You must select ${genderCount?.number_of_males} male${genderCount?.number_of_males > 1 ? 's' : ''} for ${convertEventType(formdata?.event_type)} Event`);
                        return;
                    }

                    if (femaleCount !== genderCount?.number_of_females) {
                        ErrorAlert(`You must select ${genderCount?.number_of_females} female${genderCount?.number_of_females > 1 ? 's' : ''} for ${convertEventType(formdata?.event_type)} Event`);
                        return;
                    }
                }
                if ((formdata?.event_type !== "JUNIOR_SINGLE" && formdata?.event_type !== "GROUP_CAPTAIN") && formdata?.player_details?.length !== getPlayerCount(formdata?.event_type)) {
                    ErrorAlert(`You must add ${getPlayerCount(formdata?.event_type)} player for ${convertEventType(formdata?.event_type)} Event`);
                    return
                }
                data.append('team_event_id', "")
                if (formdata?.player_details?.length > 0) {
                    formdata?.player_details?.forEach((item, index) => {
                        if (formdata?.event_type && watchCategoryType?.value === "INDIVIDUAL") {
                            data.append(`player_details[${index}][event_type]`, formdata?.event_type)
                            if (!formdata[`${formdata?.event_type}_age_group`]) {
                                ErrorAlert('You must select age group with event')
                                return
                            }
                            data.append(`player_details[${index}][age_group]`, formdata[`${formdata?.event_type}_age_group`])
                            data.append(`player_details[${index}][age_value]`, parseInt(formdata[`${formdata?.event_type}_age_value`]))
                        }
                        if (item?.player_name) {
                            data.append(`player_details[${index}][player_name]`, item?.player_name)
                        }
                        if (item?.club_name) {
                            data.append(`player_details[${index}][club_name]`, item?.club_name)
                        }
                        if (item?.player_email) {
                            data.append(`player_details[${index}][player_email]`, item?.player_email)
                        }
                        if (item?.player_contact) {
                            data.append(`player_details[${index}][player_contact]`, item?.player_contact)
                        }
                        if (item?.player_gender) {
                            data.append(`player_details[${index}][player_gender]`, item?.player_gender)
                        }
                        if (item?.player_dob) {
                            data.append(`player_details[${index}][player_dob]`, moment(item?.player_dob).format("YYYY-MM-DD"))
                        }
                        if (item?.player_idcard) {
                            data.append(`player_details[${index}][player_idcard]`, item?.player_idcard)
                        }
                        if (item?.nationality) {
                            data.append(`player_details[${index}][nationality]`, item?.nationality)
                        }
                        if (item?.dupr_rating && tournament?.sports_type === "PICKLEBALL") {
                            data.append(`player_details[${index}][dupr_rating]`, item?.dupr_rating)
                        }
                        data.append(`player_details[${index}][proffesional_player]`, item?.proffesional_player ? 1 : 0)
                        if (item?.tshirt_id?.value) {
                            data.append(`player_details[${index}][tshirt_details][tshirt_id]`, item?.tshirt_id?.value)
                            data.append(`player_details[${index}][tshirt_details][tshirt_size_id]`, item?.tshirt_size_id?.value)
                        }
                    })
                }
            } else if (watchCategoryType?.value === "TEAM") {
                data.append('team_event_id', formdata?.team_event_id?.value)
                data.append('team_name', formdata?.team_name)
                const sortedEvents = tournament?.event_details?.filter(item => item?.team_event_id === watchTeamEventId?.value)?.sort((a, b) => a?.event_type?.localeCompare(b?.event_type));

                let playerIndex = 0;

                sortedEvents?.forEach((eventItem) => {
                    const playerCount = getPlayerCount(eventItem?.event_type) || 0;
                    if (playerCount > 0) {
                        Array.from({ length: playerCount }).forEach((_, localIndex) => {
                            const currentIndex = playerIndex++;
                            data.append(`player_details[${currentIndex}][event_type]`, eventItem?.event_type)
                            data.append(`player_details[${currentIndex}][age_group]`, eventItem?.age_group)
                            data.append(`player_details[${currentIndex}][age_value]`, eventItem?.age_value)
                            if (formdata?.player_details[currentIndex]?.player_name) {
                                data.append(`player_details[${currentIndex}][player_name]`, formdata?.player_details[currentIndex]?.player_name)
                            }
                            if (formdata?.player_details[currentIndex]?.club_name) {
                                data.append(`player_details[${currentIndex}][club_name]`, formdata?.player_details[currentIndex]?.club_name)
                            }
                            if (formdata?.player_details[currentIndex]?.player_email) {
                                data.append(`player_details[${currentIndex}][player_email]`, formdata?.player_details[currentIndex]?.player_email)
                            }
                            if (formdata?.player_details[currentIndex]?.player_contact) {
                                data.append(`player_details[${currentIndex}][player_contact]`, formdata?.player_details[currentIndex]?.player_contact)
                            }
                            if (formdata?.player_details[currentIndex]?.player_gender) {
                                data.append(`player_details[${currentIndex}][player_gender]`, formdata?.player_details[currentIndex]?.player_gender)
                            }
                            if (formdata?.player_details[currentIndex]?.player_dob) {
                                data.append(`player_details[${currentIndex}][player_dob]`, moment(formdata?.player_details[currentIndex]?.player_dob).format("YYYY-MM-DD"))
                            }
                            if (formdata?.player_details[currentIndex]?.player_idcard) {
                                data.append(`player_details[${currentIndex}][player_idcard]`, formdata?.player_details[currentIndex]?.player_idcard)
                            }
                            if (formdata?.player_details[currentIndex]?.nationality) {
                                data.append(`player_details[${currentIndex}][nationality]`, formdata?.player_details[currentIndex]?.nationality)
                            }
                            if (formdata?.player_details[currentIndex]?.dupr_rating && tournament?.sports_type === "PICKLEBALL") {
                                data.append(`player_details[${currentIndex}][dupr_rating]`, formdata?.player_details[currentIndex]?.dupr_rating)
                            }
                            data.append(`player_details[${currentIndex}][proffesional_player]`, formdata?.player_details[currentIndex]?.proffesional_player ? 1 : 0)
                            if (formdata?.player_details[currentIndex]?.tshirt_id?.value) {
                                data.append(`player_details[${currentIndex}][tshirt_details][tshirt_id]`, formdata?.player_details[currentIndex]?.tshirt_id?.value)
                                data.append(`player_details[${currentIndex}][tshirt_details][tshirt_size_id]`, formdata?.player_details[currentIndex]?.tshirt_size_id?.value)
                            }
                        })
                    }
                })
            }
            data.append('tournament_id', state?.tournamentId)
            if (state?.edit) {
                playerIdList?.forEach((item, index) => {
                    data.append(`player_ids[${index}]`, item)
                })
                data.append(`team_id`, state?.player_details?.find(item => item?.team_id)?.team_id)
            }
            setLoading(true)
            let result
            if (state?.edit) {
                result = await editCustomerPlayerService({
                    id: state?.tournamentId,
                    data
                });
            }
            else {
                result = await enterTournamentService(data);
            }
            if (result?.data?.status) {
                navigate('/tournament-summary', {
                    state: {
                        tournament: result?.data?.data
                    }
                });
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
            setLoading(false)
        } catch (error) {
            setLoading(false)
            ErrorAlert(error)
        }
    }

    return <HelmetProvider>
        <Helmet prioritizeSeoTags>
            <title>Enter Tournament | AFA Tournament - Your Gateway to Sporting Excellence!</title>
        </Helmet>
        {tournament &&
            <>
                <Breadcrumb text='Enter Tournament' />
                <p className='fs-25 fw-600 mt-4'>Enter Tournament</p>
                <Form className='w-100 mb-4' onSubmit={handleSubmit(onSubmit)}>
                    {/* {tournament?.event_details?.filter(item => !item?.team_event_id)?.length > 0 && tournament?.team_event_details?.length > 0 &&
                        <Form.Group className="d-flex flex-column align-items-start my-2">
                            <Form.Label className='fs-14 fw-500'>Type of Events</Form.Label>
                            <div className='d-inline-block'>
                                <Controller
                                    name={"category_type"}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            placeholder={<p className='d-flex align-items-center mb-0'>
                                                <User className='me-2' />
                                                <Users className='me-2' />
                                                <span>Type of Events</span>
                                            </p>}
                                            styles={reactSelectCustomStyle}
                                            components={{
                                                Option: (props) => {
                                                    const { data } = props;

                                                    return (
                                                        <components.Option {...props}>
                                                            <span>
                                                                {data?.value === "INDIVIDUAL" ?
                                                                    <User color='currentcolor' className='me-2' />
                                                                    :
                                                                    data?.value === "TEAM" ?
                                                                        <Users color='currentcolor' className='me-2' />
                                                                        :
                                                                        <></>
                                                                }
                                                                {data?.value === "INDIVIDUAL" ?
                                                                    "Individuals Event"
                                                                    :
                                                                    data?.value === "TEAM" ?
                                                                        "Team Event"
                                                                        :
                                                                        <></>
                                                                }
                                                            </span>
                                                        </components.Option>
                                                    );
                                                },
                                                SingleValue: (props) => {
                                                    const { data } = props;

                                                    return (
                                                        <components.SingleValue {...props}>
                                                            <span>
                                                                {data?.value === "INDIVIDUAL" ?
                                                                    <User color='currentcolor' className='me-2' />
                                                                    :
                                                                    data?.value === "TEAM" ?
                                                                        <Users color='currentcolor' className='me-2' />
                                                                        :
                                                                        <></>
                                                                }
                                                                {data?.value === "INDIVIDUAL" ?
                                                                    "Individuals Event"
                                                                    :
                                                                    data?.value === "TEAM" ?
                                                                        "Team Event"
                                                                        :
                                                                        <></>
                                                                }
                                                            </span>
                                                        </components.SingleValue>
                                                    );
                                                }
                                            }}
                                            options={categoryTypeList?.map((item) => {
                                                return {
                                                    value: item,
                                                    label: item
                                                }
                                            })}
                                            isSearchable={false}
                                            {...field}
                                        />
                                    )}
                                    rules={{
                                        required: "Type of Event is required"
                                    }}
                                />
                                {errors?.category_type && <ErrorText>{errors?.category_type?.message}</ErrorText>}
                            </div>
                        </Form.Group>
                    } */}

                    {watchCategoryType?.value === "INDIVIDUAL" && tournament?.event_details?.filter(item => !item?.team_event_id)?.length > 0 &&
                        <>
                            <div className='d-flex justify-content-between align-items-center mb-3'>
                                <span className='fs-20 fw-600'>Players</span>
                                <div role='button' className={classNames('bg-danger bg-opacity-10 rounded py-1 px-3', {
                                    "d-none": watchCategoryType?.value === "INDIVIDUAL" && (getPlayerCount(allWatchedValues?.event_type) === allWatchedValues?.player_details?.length)
                                })} onClick={() => {
                                    append({
                                        player_name: "",
                                        club_name: "",
                                        player_email: "",
                                        player_contact: "",
                                        player_gender: "MALE",
                                        player_dob: "",
                                        player_idcard: "",
                                        nationality: "",
                                        ...(tournament?.sports_type === "PICKLEBALL" && {
                                            dupr_rating: ""
                                        }),
                                        proffesional_player: false
                                    })
                                }}>
                                    <span className='text-primary fw-bold'>+ Add Player</span>
                                </div>
                            </div>
                            <div>
                                <span className='fw-600'>Available Individual Event</span>
                                <div className='d-flex flex-wrap align-items-center mb-3'>
                                    {watchCategoryType?.value === "INDIVIDUAL" && [...new Map(tournament?.event_details?.filter(item => !item?.team_event_id)?.map(item => [item?.event_type, item])).values()]?.length > 0 && [...new Map(tournament?.event_details?.filter(item => !item?.team_event_id)?.map(item => [item?.event_type, item])).values()]?.map((item, index) => {
                                        return <div key={index}>
                                            <Form.Group className='me-2'>
                                                <Form.Check
                                                    className='event-type ps-0 mt-3'
                                                    type='radio'
                                                    label={<>
                                                        <Controller
                                                            name={`${item?.event_type}_age_group_value`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select
                                                                    placeholder={<p className='d-flex justify-content-between align-items-center mb-0'>
                                                                        <span>{convertEventType(item?.event_type)} </span>
                                                                        {tournament?.event_details?.filter(eventItem => eventItem?.event_type === item?.event_type && !eventItem?.team_event_id)?.every(eventItem => eventItem?.is_category_full || eventItem?.is_disabled) &&
                                                                            <span className='bg-event-full rounded-pill text-white px-2'>FULL</span>
                                                                        }
                                                                    </p>}
                                                                    styles={reactSelectCustomStyle}
                                                                    getOptionValue={(eventItem) =>
                                                                        `${eventItem?.age_group}|${eventItem?.age_value}`
                                                                    }
                                                                    components={{
                                                                        Option: (props) => {
                                                                            const { data } = props;

                                                                            return (
                                                                                <components.Option {...props}>
                                                                                    <span>
                                                                                        {detectMenEvent({
                                                                                            event: data?.event_type,
                                                                                            color: 'currentcolor',
                                                                                            className: 'me-2'
                                                                                        })}
                                                                                        {detectWomenEvent({
                                                                                            event: data?.event_type,
                                                                                            color: 'currentcolor',
                                                                                            className: 'me-2'
                                                                                        })}
                                                                                        {convertEventType(data?.event_type)} ({convertAgeGroup(data?.age_group, data?.age_value)})
                                                                                    </span>
                                                                                </components.Option>
                                                                            );
                                                                        },
                                                                        SingleValue: (props) => {
                                                                            const { data } = props;

                                                                            return (
                                                                                <components.SingleValue {...props}>
                                                                                    <span>
                                                                                        {detectMenEvent({
                                                                                            event: data?.event_type,
                                                                                            color: 'currentcolor',
                                                                                            className: 'me-2'
                                                                                        })}
                                                                                        {detectWomenEvent({
                                                                                            event: data?.event_type,
                                                                                            color: 'currentcolor',
                                                                                            className: 'me-2'
                                                                                        })}
                                                                                        {convertEventType(data?.event_type)} ({convertAgeGroup(data?.age_group, data?.age_value)})
                                                                                    </span>
                                                                                </components.SingleValue>
                                                                            );
                                                                        }
                                                                    }}
                                                                    options={tournament?.event_details?.filter(eventItem => eventItem?.event_type === item?.event_type && !eventItem?.team_event_id)}
                                                                    isSearchable={false}
                                                                    {...field}
                                                                    onChange={(value) => {
                                                                        _.keys(_.pickBy(allWatchedValues, (v, k) => {
                                                                            return _.includes(k, 'age_group_value');
                                                                        }))?.forEach((key) => {
                                                                            if (key !== `${item?.event_type}_age_group_value`) {
                                                                                setValue(key, null)
                                                                                setValue(key.replace('_age_group_value', '_age_group'), null)
                                                                                setValue(key.replace('_age_group_value', '_age_value'), null)
                                                                            }
                                                                        })
                                                                        setValue(`${item?.event_type}_age_group_value`, value)
                                                                        setValue(`${item?.event_type}_age_group`, value?.age_group)
                                                                        setValue(`${item?.event_type}_age_value`, value?.age_value)
                                                                        setValue(`event_type`, item?.event_type)
                                                                    }}
                                                                    isClearable={true}
                                                                />
                                                            )}
                                                            rules={{
                                                                required: allWatchedValues?.event_type === item?.event_type ? 'Age Group is required' : false
                                                            }}
                                                        />
                                                    </>
                                                    }
                                                    id={`${item?.event_type}_${index}`}
                                                    value={item?.event_type}
                                                    {...register(`event_type`)}
                                                />
                                            </Form.Group>
                                        </div>
                                    })}
                                </div>
                            </div>
                        </>
                    }

                    {watchCategoryType?.value === "TEAM" && tournament?.team_event_details?.length > 0 &&
                        <Form.Group className="d-flex flex-column align-items-start my-2">
                            <Form.Label className='fs-14 fw-500'>Available Team Event</Form.Label>
                            <div className='d-inline-block'>
                                <Controller
                                    name={"team_event_id"}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            placeholder={<p className='d-flex align-items-center mb-0'>
                                                <Men className='me-2' />
                                                <Women className='me-2' />
                                                <span>Available Team Event</span>
                                            </p>}
                                            styles={reactSelectCustomStyle}
                                            components={{
                                                Option: (props) => {
                                                    const { data } = props;

                                                    return (
                                                        <components.Option {...props}>
                                                            <span>
                                                                <Men color='currentcolor' className='me-2' />
                                                                <Women color='currentcolor' className='me-2' />
                                                                {data?.label}
                                                            </span>
                                                        </components.Option>
                                                    );
                                                },
                                                SingleValue: (props) => {
                                                    const { data } = props;

                                                    return (
                                                        <components.SingleValue {...props}>
                                                            <span>
                                                                <Men color='currentcolor' className='me-2' />
                                                                <Women color='currentcolor' className='me-2' />
                                                                {data?.label}
                                                            </span>
                                                        </components.SingleValue>
                                                    );
                                                }
                                            }}
                                            options={tournament?.team_event_details?.map((item) => {
                                                return {
                                                    value: item?.id,
                                                    label: item?.event_name
                                                }
                                            })}
                                            isSearchable={false}
                                            {...field}
                                        />
                                    )}
                                    rules={{
                                        required: "Team Event is required"
                                    }}
                                />
                            </div>
                            {errors?.team_event_id && <ErrorText>{errors?.team_event_id?.message}</ErrorText>}
                        </Form.Group>
                    }

                    {watchCategoryType?.value === "TEAM" && watchTeamEventId?.value && tournament?.team_event_details?.length > 0 &&
                        <Form.Group className='my-2'>
                            <Form.Label className='fs-14 fw-500'>Team Name</Form.Label>
                            <Controller
                                control={control}
                                name={`team_name`}
                                render={({ field }) => {
                                    return (
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter Team Name"
                                            {...field}
                                        />
                                    )
                                }}
                                rules={{
                                    required: "Team Name is required"
                                }}
                            />
                            {errors?.team_name && <ErrorText>{errors?.team_name?.message}</ErrorText>}
                        </Form.Group>
                    }

                    {watchCategoryType?.value === "TEAM" && (() => {
                        const sortedEvents = tournament?.event_details?.filter(item => item?.team_event_id === watchTeamEventId?.value)?.sort((a, b) => a?.event_type?.localeCompare(b?.event_type));

                        let playerIndex = 0;

                        return sortedEvents?.map((eventItem, eventIndex) => {
                            const playerCount = getPlayerCount(eventItem?.event_type) || 0;

                            return <div key={eventIndex}>
                                <p className="fs-20 fw-600 text-center">
                                    {convertEventType(eventItem?.event_type)} ({convertAgeGroup(eventItem?.age_group, eventItem?.age_value)})
                                </p>
                                {playerCount > 0 && Array.from({ length: playerCount }).map((_, localIndex) => {
                                    const currentIndex = playerIndex++;

                                    return <div className="mb-3 p-3 bg-secondary bg-opacity-10 border rounded" key={currentIndex}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="fs-20 fw-600">Player - {localIndex + 1}</span>
                                        </div>
                                        <PlayerForm
                                            playerIndex={currentIndex}
                                            tournament={tournament}
                                            tShirtList={tShirtList}
                                            {...{ control, register, setValue, errors }}
                                            allWatchedValues={allWatchedValues}
                                        />
                                    </div>
                                })}
                            </div>
                        });
                    })()}

                    {watchCategoryType?.value === "INDIVIDUAL" && fields.map((item, index) => {
                        return <div className='mb-3 p-3 bg-secondary bg-opacity-10 border rounded' key={item?.id}>
                            <div className='d-flex justify-content-between align-items-center'>
                                <span className='fs-20 fw-600'>Player - {index + 1}</span>
                                {!state?.edit &&
                                    allWatchedValues?.player_details?.length > 1 && <IoMdClose role='button' color='red' size={20} onClick={() => remove(index)} />
                                }
                            </div>
                            <PlayerForm
                                playerIndex={index}
                                tournament={tournament}
                                tShirtList={tShirtList}
                                {...{ control, register, setValue, errors }}
                                allWatchedValues={allWatchedValues}

                            />
                        </div>
                    })}

                    <div className='d-flex justify-content-end align-items-end mt-4'>
                        <div>
                            <PrimaryButton className='fs-14 fw-500 px-5 py-2' disabled={loading || allWatchedValues?.player_details?.length === 0} loading={loading} type="submit">Enter Tournament</PrimaryButton>
                        </div>
                    </div>
                </Form>
            </>
        }
    </HelmetProvider>
};

export default EnterTournament;